<template>
  <div class="container my-5">
    <div class="row justify-content-center">
      <div class="col-12">
        <!-- Loading spinner for initial page load -->
        <div v-if="isPageLoading" class="text-center py-5">
          <div class="spinner-border text-warning" role="status">
            <span class="visually-hidden">Ładowanie...</span>
          </div>
          <p class="mt-3 text-muted">Pobieranie profilu użytkownika...</p>
        </div>

        <div v-else-if="authStore.user">
          <!-- Profile Info Card -->
          <div class="card mb-4 shadow-lg border-0">
            <div class="card-body p-4">
              <h3 class="card-title text-warning mb-4 d-flex align-items-center">
                <font-awesome-icon icon="fa-solid fa-id-card" class="me-2" />
                Mój Profil
              </h3>

              <div class="row mb-3 align-items-center">
                <div class="col-sm-4 col-md-3 fw-bold text-muted">
                  <font-awesome-icon icon="fa-solid fa-at" class="me-2" />
                  Email
                </div>
                <div class="col-sm-8 col-md-9 text-body-emphasis">
                  {{ profileData.Email || authStore.user.email }}
                </div>
              </div>

              <div class="row mb-3 align-items-center">
                <div class="col-sm-4 col-md-3 fw-bold text-muted">
                  <font-awesome-icon icon="fa-solid fa-phone" class="me-2" />
                  Numer telefonu
                </div>
                <div class="col-sm-8 col-md-9 text-body-emphasis">
                  <input
                      type="tel"
                      id="phone"
                      class="form-control form-control-sm phone-input"
                      v-model="phone"
                      placeholder="np. +48666333111"
                      required
                  />
                </div>
              </div>

              <hr class="my-3 opacity-25" />

              <div class="row align-items-center g-2">
                <div class="col-auto d-flex align-items-center gap-2">
                  <button
                      type="submit"
                      class="btn btn-warning btn-sm fw-bold px-3 text-nowrap"
                      :disabled="isSaving"
                      @click="handleSave"
                  >
                      <span
                          v-if="isSaving"
                          class="spinner-border spinner-border-sm me-1"
                      ></span>
                    <font-awesome-icon v-else icon="fa-solid fa-floppy-disk" class="me-1" />
                    Zapisz
                  </button>
                </div>
              </div>

              <div v-if="successMessage" class="alert alert-success py-2 mt-3 d-flex align-items-center mb-0">
                <font-awesome-icon icon="fa-solid fa-circle-check" class="me-2" />
                {{ successMessage }}
              </div>

              <div v-if="errorMessage" class="alert alert-danger py-2 mt-3 d-flex align-items-center mb-0">
                <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="me-2" />
                {{ errorMessage }}
              </div>
            </div>
          </div>

          <!-- Reservations History Section -->
          <div class="card shadow-lg border-0">
            <div class="card-body p-4">
              <h4 class="card-title text-warning mb-4 d-flex align-items-center">
                <font-awesome-icon icon="fa-solid fa-calendar-check" class="me-2" />
                Historia Rezerwacji
              </h4>

              <div v-if="!profileData.ReservationsHistory || profileData.ReservationsHistory.length === 0" class="text-center py-4 text-muted">
                <font-awesome-icon icon="fa-solid fa-calendar-xmark" size="2x" class="mb-2 opacity-50" />
                <p class="mb-0">Brak historii rezerwacji</p>
              </div>

              <div v-else class="accordion" id="reservationsAccordion">
                <!-- Future / Upcoming Reservations -->
                <div
                    v-for="(res, index) in futureReservations"
                    :key="res.Uuid || 'future-' + index"
                    class="accordion-item border rounded mb-2 overflow-hidden"
                >
                  <h2 class="accordion-header" :id="'heading-future-' + index">
                    <button
                        class="accordion-button collapsed py-3"
                        type="button"
                        data-bs-toggle="collapse"
                        :data-bs-target="'#collapse-future-' + index"
                        aria-expanded="false"
                        :aria-controls="'#collapse-future-' + index"
                    >
                      <div class="d-flex flex-wrap align-items-center w-100 me-3 justify-content-between gap-2">
                        <!-- Left: Status Icon + Title (Type & Name) + Date & Day Name -->
                        <div class="d-flex align-items-center gap-2">
                          <div
                              class="status-icon-badge d-flex align-items-center justify-content-center p-2 rounded-3 me-1"
                              :class="{
                                'bg-danger-subtle': isCancelled(res),
                                'bg-warning-subtle': !isCancelled(res) && !isValidDate(res.PaidAt),
                                'bg-success-subtle': !isCancelled(res) && isValidDate(res.PaidAt)
                              }"
                          >
                            <font-awesome-icon
                                v-if="isCancelled(res)"
                                icon="fa-solid fa-circle-xmark"
                                class="text-danger fs-5"
                                title="Anulowano"
                            />
                            <font-awesome-icon
                                v-else-if="!isValidDate(res.PaidAt)"
                                icon="fa-solid fa-triangle-exclamation"
                                class="text-warning fs-5"
                                title="Nieopłacono"
                            />
                            <font-awesome-icon
                                v-else
                                icon="fa-solid fa-circle-check"
                                class="text-success fs-5"
                                title="Opłacono"
                            />
                          </div>

                          <div>
                            <span class="fw-bold d-block text-body-emphasis">
                              <font-awesome-icon :icon="getReservationIcon(res.Type)" class="me-1 text-warning" />
                              {{ formatReservationTitle(res) }}
                            </span>
                            <small class="text-muted text-capitalize">
                              <font-awesome-icon icon="fa-solid fa-calendar-day" class="me-1" />
                              {{ formatDate(res.StartDate) }} ({{ getDayName(res.StartDate) }})
                            </small>
                          </div>
                        </div>

                        <!-- Right: Time, Payment Warning & Status Badges -->
                        <div class="d-flex align-items-center gap-2">
                          <span class="badge bg-secondary-subtle text-body">
                            <font-awesome-icon icon="fa-solid fa-clock" class="me-1" />
                            {{ formatTime(res.StartDate) }} - {{ formatTime(res.EndDate) }}
                          </span>
                          <span v-if="!isCancelled(res) && !isValidDate(res.PaidAt)" class="badge bg-warning text-dark">
                            <font-awesome-icon icon="fa-solid fa-clock" class="me-1" />
                            Nieopłacono
                          </span>
                          <span v-if="isCancelled(res)" class="badge bg-danger">
                            Anulowano
                          </span>
                        </div>
                      </div>
                    </button>
                  </h2>

                  <div
                      :id="'collapse-future-' + index"
                      class="accordion-collapse collapse"
                      :aria-labelledby="'heading-future-' + index"
                      data-bs-parent="#reservationsAccordion"
                  >
                    <div class="accordion-body bg-body-tertiary">
                      <div class="row g-3">
                        <div class="col-sm-6 col-md-4">
                          <div class="small text-muted text-uppercase fw-bold">Typ</div>
                          <div class="text-body-emphasis">
                            <font-awesome-icon :icon="getReservationIcon(res.Type)" class="me-1 text-warning" />
                            {{ formatType(res.Type) }}
                          </div>
                        </div>

                        <div v-if="res.Name" class="col-sm-6 col-md-4">
                          <div class="small text-muted text-uppercase fw-bold">Nazwa</div>
                          <div class="text-body-emphasis fw-bold">{{ res.Name }}</div>
                        </div>

                        <div class="col-sm-6 col-md-4">
                          <div class="small text-muted text-uppercase fw-bold">ID</div>
                          <div class="text-body-emphasis font-monospace text-break">{{ res.Uuid }}</div>
                        </div>

                        <div class="col-sm-6 col-md-4">
                          <div class="small text-muted text-uppercase fw-bold">Kwota</div>
                          <div class="text-body-emphasis fw-bold text-success">
                            {{ res.Amount ?? 0 }} {{ res.Currency || '-' }}
                          </div>
                        </div>

                        <div class="col-sm-6 col-md-4">
                          <div class="small text-muted text-uppercase fw-bold">Data rozpoczęcia</div>
                          <div class="text-body-emphasis">{{ formatDateTime(res.StartDate) }}</div>
                        </div>

                        <div class="col-sm-6 col-md-4">
                          <div class="small text-muted text-uppercase fw-bold">Data zakończenia</div>
                          <div class="text-body-emphasis">{{ formatDateTime(res.EndDate) }}</div>
                        </div>

                        <div class="col-sm-6 col-md-4">
                          <div class="small text-muted text-uppercase fw-bold">Płatność</div>
                          <div class="text-body-emphasis">
                            <span :class="isValidDate(res.PaidAt) ? 'text-success' : 'text-warning'">
                              <font-awesome-icon :icon="isValidDate(res.PaidAt) ? 'fa-solid fa-circle-check' : 'fa-solid fa-clock'" class="me-1" />
                              {{ isValidDate(res.PaidAt) ? 'Opłacono (' + formatDateTime(res.PaidAt) + ')' : 'Nieopłacono' }}
                            </span>
                          </div>
                        </div>

                        <div v-if="isCancelled(res)" class="col-12 mt-2">
                          <div class="alert alert-danger mb-0 py-2 small">
                            <font-awesome-icon icon="fa-solid fa-ban" class="me-1" />
                            <strong>Anulowano:</strong> {{ formatDateTime(res.CanceledAt) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Separator line for past reservations -->
                <div v-if="pastReservations.length > 0" class="position-relative my-4 text-center">
                  <hr class="border-secondary opacity-25 m-0" />
                  <span class="position-absolute top-50 start-50 translate-middle bg-body px-3 text-muted small fw-bold text-uppercase">
                    <font-awesome-icon icon="fa-solid fa-arrow-down" class="me-1" /> zakończone rezerwacje <font-awesome-icon icon="fa-solid fa-arrow-down" class="me-1" />
                  </span>
                </div>

                <!-- Past Reservations -->
                <div
                    v-for="(res, index) in pastReservations"
                    :key="res.Uuid || 'past-' + index"
                    class="accordion-item border rounded mb-2 overflow-hidden opacity-75"
                >
                  <h2 class="accordion-header" :id="'heading-past-' + index">
                    <button
                        class="accordion-button collapsed py-3"
                        type="button"
                        data-bs-toggle="collapse"
                        :data-bs-target="'#collapse-past-' + index"
                        aria-expanded="false"
                        :aria-controls="'#collapse-past-' + index"
                    >
                      <div class="d-flex flex-wrap align-items-center w-100 me-3 justify-content-between gap-2">
                        <!-- Left: Status Icon + Title (Type & Name) + Date & Day Name -->
                        <div class="d-flex align-items-center gap-2">
                          <div
                              class="status-icon-badge d-flex align-items-center justify-content-center p-2 rounded-3 me-1"
                              :class="{
                                'bg-danger-subtle': isCancelled(res),
                                'bg-warning-subtle': !isCancelled(res) && !isValidDate(res.PaidAt),
                                'bg-success-subtle': !isCancelled(res) && isValidDate(res.PaidAt)
                              }"
                          >
                            <font-awesome-icon
                                v-if="isCancelled(res)"
                                icon="fa-solid fa-circle-xmark"
                                class="text-danger fs-5"
                                title="Anulowano"
                            />
                            <font-awesome-icon
                                v-else-if="!isValidDate(res.PaidAt)"
                                icon="fa-solid fa-triangle-exclamation"
                                class="text-warning fs-5"
                                title="Nieopłacono"
                            />
                            <font-awesome-icon
                                v-else
                                icon="fa-solid fa-circle-check"
                                class="text-success fs-5"
                                title="Opłacono"
                            />
                          </div>

                          <div>
                            <span class="fw-bold d-block text-body-emphasis">
                              <font-awesome-icon :icon="getReservationIcon(res.Type)" class="me-1 text-warning" />
                              {{ formatReservationTitle(res) }}
                            </span>
                            <small class="text-muted text-capitalize">
                              <font-awesome-icon icon="fa-solid fa-calendar-day" class="me-1" />
                              {{ formatDate(res.StartDate) }} ({{ getDayName(res.StartDate) }})
                            </small>
                          </div>
                        </div>

                        <!-- Right: Time, Payment Warning & Status Badges -->
                        <div class="d-flex align-items-center gap-2">
                          <span class="badge bg-secondary-subtle text-body">
                            <font-awesome-icon icon="fa-solid fa-clock" class="me-1" />
                            {{ formatTime(res.StartDate) }} - {{ formatTime(res.EndDate) }}
                          </span>
                          <span v-if="!isCancelled(res) && !isValidDate(res.PaidAt)" class="badge bg-warning text-dark">
                            <font-awesome-icon icon="fa-solid fa-clock" class="me-1" />
                            Nieopłacono
                          </span>
                          <span v-if="isCancelled(res)" class="badge bg-danger">
                            Anulowano
                          </span>
                        </div>
                      </div>
                    </button>
                  </h2>

                  <div
                      :id="'collapse-past-' + index"
                      class="accordion-collapse collapse"
                      :aria-labelledby="'heading-past-' + index"
                      data-bs-parent="#reservationsAccordion"
                  >
                    <div class="accordion-body bg-body-tertiary">
                      <div class="row g-3">
                        <div class="col-sm-6 col-md-4">
                          <div class="small text-muted text-uppercase fw-bold">Typ</div>
                          <div class="text-body-emphasis">
                            <font-awesome-icon :icon="getReservationIcon(res.Type)" class="me-1 text-warning" />
                            {{ formatType(res.Type) }}
                          </div>
                        </div>

                        <div v-if="res.Name" class="col-sm-6 col-md-4">
                          <div class="small text-muted text-uppercase fw-bold">Nazwa</div>
                          <div class="text-body-emphasis fw-bold">{{ res.Name }}</div>
                        </div>

                        <div class="col-sm-6 col-md-4">
                          <div class="small text-muted text-uppercase fw-bold">ID</div>
                          <div class="text-body-emphasis font-monospace text-break">{{ res.Uuid }}</div>
                        </div>

                        <div class="col-sm-6 col-md-4">
                          <div class="small text-muted text-uppercase fw-bold">Kwota</div>
                          <div class="text-body-emphasis fw-bold text-success">
                            {{ res.Amount ?? 0 }} {{ res.Currency || '-' }}
                          </div>
                        </div>

                        <div class="col-sm-6 col-md-4">
                          <div class="small text-muted text-uppercase fw-bold">Data rozpoczęcia</div>
                          <div class="text-body-emphasis">{{ formatDateTime(res.StartDate) }}</div>
                        </div>

                        <div class="col-sm-6 col-md-4">
                          <div class="small text-muted text-uppercase fw-bold">Data zakończenia</div>
                          <div class="text-body-emphasis">{{ formatDateTime(res.EndDate) }}</div>
                        </div>

                        <div class="col-sm-6 col-md-4">
                          <div class="small text-muted text-uppercase fw-bold">Płatność</div>
                          <div class="text-body-emphasis">
                            <span :class="isValidDate(res.PaidAt) ? 'text-success' : 'text-warning'">
                              <font-awesome-icon :icon="isValidDate(res.PaidAt) ? 'fa-solid fa-circle-check' : 'fa-solid fa-clock'" class="me-1" />
                              {{ isValidDate(res.PaidAt) ? 'Opłacono (' + formatDateTime(res.PaidAt) + ')' : 'Nieopłacono' }}
                            </span>
                          </div>
                        </div>

                        <div v-if="isCancelled(res)" class="col-12 mt-2">
                          <div class="alert alert-danger mb-0 py-2 small">
                            <font-awesome-icon icon="fa-solid fa-ban" class="me-1" />
                            <strong>Anulowano:</strong> {{ formatDateTime(res.CanceledAt) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="alert alert-warning text-center">
          <font-awesome-icon icon="fa-solid fa-lock" class="me-2" />
          Musisz być zalogowany, aby zobaczyć profil.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth'
import { fetchWrapper } from '@/wrappers/api/fetchWrapper'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

export default {
  name: 'ProfilePage',
  components: {FontAwesomeIcon},
  setup() {
    const authStore = useAuthStore()
    return { authStore }
  },
  data() {
    return {
      profileData: {
        Email: '',
        Phone: '',
        ReservationsHistory: []
      },
      phone: '',
      isPageLoading: true,
      isSaving: false,
      successMessage: '',
      errorMessage: ''
    }
  },
  computed: {
    futureReservations() {
      const now = new Date()
      return (this.profileData.ReservationsHistory || [])
          .filter(res => new Date(res.StartDate) >= now)
          .sort((a, b) => new Date(a.StartDate) - new Date(b.StartDate))
    },
    pastReservations() {
      const now = new Date()
      return (this.profileData.ReservationsHistory || [])
          .filter(res => new Date(res.StartDate) < now)
          .sort((a, b) => new Date(b.StartDate) - new Date(a.StartDate))
    }
  },
  async mounted() {
    if (this.authStore.user) {
      await this.fetchProfile()
    } else {
      this.isPageLoading = false
    }
  },
  methods: {
    async fetchProfile() {
      this.isPageLoading = true
      try {
        const response = await fetchWrapper.get('/user/profile')
        if (response) {
          this.profileData = response
          this.phone = response.Phone || response.phone || ''
        }
      } catch (err) {
        this.errorMessage = err || 'Nie udało się pobrać danych profilu.'
      } finally {
        this.isPageLoading = false
      }
    },
    async handleSave() {
      this.isSaving = true
      this.successMessage = ''
      this.errorMessage = ''

      try {
        const response = await fetchWrapper.post('/user/profile/update', {
          phone: this.phone
        })

        if (this.authStore.user) {
          this.authStore.setUser({
            ...this.authStore.user,
            phone: this.phone
          })
        }

        this.profileData.Phone = this.phone
        this.successMessage = response?.message || 'Dane zostały zapisane pomyślnie!'
      } catch (err) {
        this.errorMessage = err || 'Wystąpił błąd podczas zapisywania danych.'
      } finally {
        this.isSaving = false
      }
    },
    isCancelled(reservation) {
      return this.isValidDate(reservation?.CanceledAt)
    },
    isValidDate(dateStr) {
      if (!dateStr || dateStr.startsWith('0001-01-01')) return false
      const d = new Date(dateStr)
      return !isNaN(d.getTime()) && d.getFullYear() > 1
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pl-PL')
    },
    formatTime(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return isNaN(d.getTime()) ? '-' : d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
    },
    getDayName(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pl-PL', { weekday: 'long' })
    },
    formatType(typeStr) {
      return (typeStr && typeStr.toLowerCase() === 'event') ? 'Wydarzenie' : 'Stanowisko'
    },
    getReservationIcon(typeStr) {
      return (typeStr && typeStr.toLowerCase() === 'event') ? 'fa-solid fa-bullhorn' : 'fa-solid fa-bullseye'
    },
    formatReservationTitle(res) {
      const typeLabel = this.formatType(res?.Type)
      return res?.Name ? `${typeLabel}: ${res.Name}` : typeLabel
    },
    formatDateTime(dateStr) {
      if (!dateStr || !this.isValidDate(dateStr)) return '-'
      const d = new Date(dateStr)
      return `${d.toLocaleDateString('pl-PL')} ${d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`
    }
  }
}
</script>

<style scoped>
.container {
  max-width: 1320px;
}

.status-icon-badge {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
}

.card {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.phone-input {
  max-width: 220px;
}

.accordion-button:not(.collapsed) {
  background-color: rgba(240, 192, 64, 0.1);
  color: inherit;
  box-shadow: none;
}

.accordion-button:focus {
  box-shadow: none;
  border-color: rgba(240, 192, 64, 0.5);
}

.form-control:focus {
  border-color: #f0c040;
  box-shadow: 0 0 0 0.25rem rgba(240, 192, 64, 0.25);
}

.btn-warning {
  background-color: #f0c040;
  border-color: #f0c040;
}

.btn-warning:hover {
  background-color: #ffd966;
  border-color: #ffd966;
}
</style>